import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { StoreContext } from '../Hooks/store';
import useUserInteractionTracker from '../Hooks/useUserInteractionTracker';
import {
  Typography,
  Snackbar,
  Switch, 
  FormControlLabel,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { createTodoList, archiveTodoList, deleteTodoList, updateTodoListName } from '../../api/todo';
import PropTypes from 'prop-types';
import TodoListItem from './components/TodoListItem';
import { useStyles } from './styles/todoListStyles';
import { useTodoListState } from './hooks/useTodoListState';
import DeleteConfirmationDialog from './components/DeleteConfirmationDialog';
import CreateTodoList from './components/CreateTodoList';

// Alert component for Snackbar
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function ArchivedTodolists({ todolists: propsTodoLists, onSelectTodoList, onCreateTodoList }) {
  const {
    todolistheader,
    todolistheaderid,
    showarchiveflag,
    thisuserid,
    mytodolists,
  } = React.useContext(StoreContext);

  const [todolists, settodolists] = mytodolists;

  // Initialize the user interaction tracker
  const { trackInteraction } = useUserInteractionTracker();

  const classes = useStyles();

  const [showarchived, setShowarchived] = showarchiveflag;
  const [userid] = thisuserid;
  const [, settodolistid] = todolistheaderid;
  const [, setTodoHeader] = todolistheader;

  // State for creating a new todo list
  const [newListName, setNewListName] = useState('');
  const [isCreatingList, setIsCreatingList] = useState(propsTodoLists.length === 0);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [listToDelete, setListToDelete] = useState(null);

  // Edit list name state
  const [editingListId, setEditingListId] = useState(null);
  const [editedListName, setEditedListName] = useState('');
  
  // Get showComments and commentsListId from global state
  const [showComments, setShowComments] = React.useContext(StoreContext).showComments;
  const [commentsListId, setCommentsListId] = React.useContext(StoreContext).commentsListId;

  // State for showing/hiding archived lists
  const [showArchivedLists, setShowArchivedLists] = useState(
    localStorage.getItem('showArchivedLists') === 'true' || false
  );

  // Use custom hook for todo list state management
  const { todoListCounts, mostRecentLists, groupedLists, updateSingleList } = useTodoListState(propsTodoLists);

  // Track todo_lists_view when the component mounts
  useEffect(() => {
    if (userid) {
      trackInteraction('todo_lists_overview', {
        componentName: 'ArchivedTodolists',
        timestamp: Date.now(),
        userId: userid,
        listCount: todolists?.length || 0
      });
    }
  }, [userid]);

  // Toggle archived lists visibility
  const handleToggleArchivedLists = () => {
    setShowArchivedLists((prev) => {
      const newValue = !prev;
      localStorage.setItem('showArchivedLists', newValue);
      return newValue;
    });
  };

  // Show Snackbar function
  const showSnackbar = (message, severity = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Handle Snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Handle creating a new todo list
  const queryClient = useQueryClient();

  const handleCreateTodoList = async (description = '') => {
    if (newListName.trim() === '') {
      showSnackbar('List name cannot be empty', 'error');
      return;
    }

    try {
      const newList = await createTodoList(userid, newListName, description);
      
      // Track the create action
      trackInteraction('todolist_create', {
        componentName: 'ArchivedTodolists',
        timestamp: Date.now(),
        userId: userid
      });
      
      // Update local state in context
      settodolists(prev => [...prev.filter(list => list.id !== newList.id), newList]);
      
      // Directly update the groupedLists state using updateSingleList
      updateSingleList(newList);
      
      // Force refresh of all lists
      await queryClient.invalidateQueries(['todoLists', userid]);
      await queryClient.refetchQueries(['todoLists', userid]);
      
      // Update parent state if callback provided
      if (typeof onCreateTodoList === 'function') {
        onCreateTodoList(newList);
      }
      
      // Reset form and show success
      setNewListName('');
      setIsCreatingList(false);
      showSnackbar('New list created!', 'success');
      
      // Select the newly created list
      settodolistid(newList.id);
      setTodoHeader(newList.list_name);
    } catch (error) {
      console.error('Error creating todo list:', error);
      showSnackbar('Error creating todo list', 'error');
    }
  };

  // Handle archiving/unarchiving a todo list
  const handleArchiveTodoList = async (listId, newArchivedStatus) => {
    try {
      // Optimistically update local state first
      const updatedLists = todolists.map((list) => {
        if (list.id === listId) {
          return { ...list, archived: newArchivedStatus };
        }
        return list;
      });
      settodolists(updatedLists);
      
      // Update in database
      const result = await archiveTodoList(listId, newArchivedStatus);
      
      if (!result) {
        throw new Error('No response from server');
      }
      
      // Find the list name for tracking
      const listName = todolists.find(list => list.id === listId)?.list_name || '';
      
      // Track the archive/unarchive action
      trackInteraction(newArchivedStatus ? 'todolist_archive' : 'todolist_unarchive', {
        componentName: 'ArchivedTodolists',
        timestamp: Date.now(),
        userId: userid,
        listId,
        listName
      });
      
      // Find the todolist in propsTodoLists to update
      const todolistToUpdate = propsTodoLists.find(list => list.id === listId);
      if (todolistToUpdate) {
        // Create an updated version of the todolist
        const updatedTodolist = {
          ...todolistToUpdate,
          archived: newArchivedStatus
        };
        
        // Directly update the groupedLists state using updateSingleList
        updateSingleList(updatedTodolist);
      }
      
      // Force refresh to ensure sync
      await queryClient.invalidateQueries(['todoLists', userid]);
      await queryClient.refetchQueries(['todoLists', userid]);
      
      return result; // Return the API response
    } catch (error) {
      console.error('Error archiving/unarchiving list:', error);
      // Don't show snackbar here, let the calling component handle it
      throw error; // Re-throw the error so the calling component can handle it
    }
  };

  // Handle deleting a todo list
  const handleDeleteTodoList = async () => {
    if (!listToDelete) return;

    try {
      // Find the todolist in propsTodoLists to remove
      const todolistToDelete = propsTodoLists.find(list => list.id === listToDelete);
      
      // Optimistically update local state first
      const updatedLists = todolists.filter((list) => list.id !== listToDelete);
      settodolists(updatedLists);
      
      // Track the delete action
      trackInteraction('todolist_delete', {
        componentName: 'ArchivedTodolists',
        timestamp: Date.now(),
        userId: userid
      });
      
      // Delete from database
      await deleteTodoList(listToDelete);
      
      // If we found the todolist in propsTodoLists, update the groupedLists state
      if (todolistToDelete) {
        // Create a dummy updated version with a flag to indicate deletion
        const updatedTodolist = {
          ...todolistToDelete,
          _deleted: true // Special flag to indicate deletion
        };
        
        // Directly update the groupedLists state using updateSingleList
        updateSingleList(updatedTodolist);
      }
      
      showSnackbar('List deleted!', 'success');
      
      // Force refresh of all lists
      await queryClient.invalidateQueries(['todoLists', userid]);
      await queryClient.refetchQueries(['todoLists', userid]);
    } catch (error) {
      console.error('Error deleting list:', error);
      // Rollback optimistic update
      settodolists(todolists);
      showSnackbar('Error deleting list', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setListToDelete(null);
    }
  };

  // Handle editing a todo list name
  const handleEditListName = React.useCallback((listId, currentName) => {
    // Find the list in both propsTodoLists and context todolists to get the most up-to-date name
    const propsListName = propsTodoLists.find(list => list.id === listId)?.list_name;
    const contextListName = todolists.find(list => list.id === listId)?.list_name;
    
    // Use the most up-to-date name (prefer props as it should be fresh from the server)
    const mostUpToDateName = propsListName || contextListName || currentName;
    
    setEditingListId(listId);
    setEditedListName(mostUpToDateName);
  }, [propsTodoLists, todolists]);

  // Handle saving the edited list name
  const handleSaveListName = async (listId, description = null) => {
    if (editedListName.trim() === '') {
      showSnackbar('List name cannot be empty', 'error');
      return;
    }

    try {
      // Find the original list name for tracking
      const originalListName = todolists.find(list => list.id === listId)?.list_name || '';
      
      // Update the database
      await updateTodoListName(listId, editedListName, description);
      
      // Track the edit action
      trackInteraction('todolist_edit', {
        componentName: 'ArchivedTodolists',
        timestamp: Date.now(),
        userId: userid
      });
      
      // Update local state in context
      const updatedLists = todolists.map((list) =>
        list.id === listId ? { ...list, list_name: editedListName, description } : list
      );
      settodolists(updatedLists);
      
      // Find the todolist in propsTodoLists to update
      const todolistToUpdate = propsTodoLists.find(list => list.id === listId);
      if (todolistToUpdate) {
        // Create an updated version of the todolist with a deep clone
        const updatedTodolist = JSON.parse(JSON.stringify({
          ...todolistToUpdate,
          list_name: editedListName,
          description
        }));
        
        // Directly update the groupedLists state using updateSingleList
        updateSingleList(updatedTodolist);
      } else {
        console.warn('Could not find todolist in propsTodoLists:', listId);
      }
      
      // Update propsTodoLists by forcing a refetch
      await queryClient.resetQueries(['todoLists', userid], { exact: true });
      
      // Use refetchQueries instead of fetchQuery to avoid the "Missing queryFn" error
      const result = await queryClient.refetchQueries({
        queryKey: ['todoLists', userid],
        exact: true,
        type: 'active'
      });
      
      setEditedListName('');
      showSnackbar('List updated!', 'success');
      
      return true; // Return success for the calling component to handle UI updates
    } catch (error) {
      console.error('Error updating list:', error);
      showSnackbar('Error updating list', 'error');
      throw error; // Re-throw to allow the calling component to handle errors
    }
  };

  // Render todo list items by group
  const renderTodoListItems = (lists) => {
    return lists.map((todolist) => (
      <TodoListItem
        key={todolist.id}
        todolist={todolist}
        todoListCounts={todoListCounts}
        onSelectTodoList={onSelectTodoList}
        handleArchiveTodoList={handleArchiveTodoList}
        handleDeleteConfirmation={(id) => {
          setListToDelete(id);
          setDeleteDialogOpen(true);
        }}
        handleEditListName={handleEditListName}
        handleSaveListName={handleSaveListName}
        editingListId={editingListId}
        setEditingListId={setEditingListId}
        editedListName={editedListName}
        setEditedListName={setEditedListName}
        classes={classes}
        settodolistid={settodolistid}
        setTodoHeader={setTodoHeader}
        showSnackbar={showSnackbar}
      />
    ));
  };

  return (
    <div style={{ height: 300, width: '100%' }}>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setListToDelete(null);
        }}
        onConfirm={async () => {
          try {
            await handleDeleteTodoList();
          } catch (error) {
            console.error('Error in delete confirmation:', error);
          }
        }}
      />

      {/* Toggle for showing/hiding archived lists */}
      <FormControlLabel
        control={
          <Switch
            checked={showArchivedLists}
            onChange={handleToggleArchivedLists}
            color="primary"
          />
        }
        label={showArchivedLists ? 'Hide Archived Lists' : 'Show Archived Lists'}
      />

      {/* Display a message when archived lists are hidden */}
      {!showArchivedLists && (
        <Typography variant="body2" color="textSecondary">
          {propsTodoLists.filter((list) => list.archived).length} archived lists hidden
        </Typography>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h2>{propsTodoLists.length === 0 ? 'Create Your First Todo List' : 'All To-Do Lists'}</h2>
        
        {/* Always show Create List button when not already creating */}
        {propsTodoLists.length > 0 && !isCreatingList && (
          <CreateTodoList
            isCreating={false}
            newListName={newListName}
            onNameChange={(e) => setNewListName(e.target.value)}
            onCreateList={handleCreateTodoList}
            onCancel={() => {
              setIsCreatingList(false);
              setNewListName('');
            }}
            onStartCreating={() => {
              setIsCreatingList(true);
              setNewListName('');
            }}
            classes={classes}
          />
        )}
      </div>

      {/* Create New Todo List form - show when creating first list or when isCreatingList is true */}
      {(propsTodoLists.length === 0 || isCreatingList) && (
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          {propsTodoLists.length === 0 && (
            <Typography variant="body2" color="textSecondary" paragraph>
              First, create a task list (like a bucket for your todos). Then, add your tasks to the list.
            </Typography>
          )}
          <CreateTodoList
            isCreating={true}
            newListName={newListName}
            onNameChange={(e) => setNewListName(e.target.value)}
            onCreateList={handleCreateTodoList}
            onCancel={() => {
              // For users with existing lists, allow canceling the creation
              if (propsTodoLists.length > 0) {
                setIsCreatingList(false);
              }
              setNewListName('');
            }}
            onStartCreating={() => {
              setIsCreatingList(true);
              setNewListName('');
            }}
            classes={classes}
          />
        </div>
      )}

      {/* List of Todo Lists - only show if there are lists */}
      {propsTodoLists.length > 0 && (
        <div>
          {/* Today */}
          {groupedLists.today.length > 0 && (
            <React.Fragment key="today">
              <Typography variant="h6">Today</Typography>
              {renderTodoListItems(groupedLists.today)}
            </React.Fragment>
          )}

          {/* Yesterday */}
          {groupedLists.yesterday.length > 0 && (
            <React.Fragment>
              <Typography variant="h6">Yesterday</Typography>
              {renderTodoListItems(groupedLists.yesterday)}
            </React.Fragment>
          )}

          {/* Previous 7 Days */}
          {groupedLists.previous7Days.length > 0 && (
            <React.Fragment>
              <Typography variant="h6">Previous 7 Days</Typography>
              {renderTodoListItems(groupedLists.previous7Days)}
            </React.Fragment>
          )}

          {/* Previous 30 Days */}
          {groupedLists.previous30Days.length > 0 && (
            <React.Fragment>
              <Typography variant="h6">Previous 30 Days</Typography>
              {renderTodoListItems(groupedLists.previous30Days)}
            </React.Fragment>
          )}

          {/* By Month (Older than 30 Days) */}
          {Object.entries(groupedLists.byMonth).map(([monthYear, lists]) => (
            <div key={monthYear}>
              <Typography variant="h6">{monthYear}</Typography>
              {renderTodoListItems(lists)}
            </div>
          ))}

          {/* Archived Lists */}
          {showArchivedLists && (
            <React.Fragment>
              <Typography variant="h6">Archived</Typography>
              {renderTodoListItems(groupedLists.archived)}
            </React.Fragment>
          )}
        </div>
      )}
    </div>
  );
}

ArchivedTodolists.propTypes = {
  todolists: PropTypes.array.isRequired,
  onSelectTodoList: PropTypes.func,
  onCreateTodoList: PropTypes.func,
};

ArchivedTodolists.defaultProps = {
  todolists: [],
  onSelectTodoList: () => {},
  onCreateTodoList: () => {},
};
